/**
 * UsageErrorState
 *
 * Shared error panel for the Slide Sense usage views (dashboard + node drawer).
 * Centralised so both surfaces stay visually identical and the i18n keys are
 * reused.
 */

import { useTranslation } from "react-i18next"

import { AlertTriangle, RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface UsageErrorStateProps {
  /** Triggered when the user clicks "Retry". */
  onRetry: () => void
  /** Disables the retry button while a refetch is already in flight. */
  isRetrying?: boolean
  /** Optional override for the heading copy. */
  title?: string
  /** Optional override for the description copy. */
  description?: string
  /** Visual density — `compact` removes padding for tight surfaces (drawer). */
  variant?: "default" | "compact"
  className?: string
}

export function UsageErrorState({
  onRetry,
  isRetrying = false,
  title,
  description,
  variant = "default",
  className,
}: UsageErrorStateProps) {
  const { t } = useTranslation()
  const isCompact = variant === "compact"

  return (
    <div
      role="alert"
      className={cn(
        "border-destructive/30 bg-destructive/5 flex flex-col items-center justify-center rounded-lg border text-center",
        isCompact ? "p-4" : "rounded-lg p-10",
        className
      )}
    >
      <AlertTriangle
        aria-hidden
        className={cn("text-destructive mb-3", isCompact ? "h-6 w-6" : "h-8 w-8")}
      />
      <h3 className={cn("font-semibold", isCompact ? "text-sm" : "text-base")}>
        {title ?? t("slideSense.usage.errorTitle")}
      </h3>
      <p className={cn("text-muted-foreground mt-1 max-w-md", isCompact ? "text-xs" : "text-sm")}>
        {description ?? t("slideSense.usage.errorDescription")}
      </p>
      <Button className="mt-4" disabled={isRetrying} size="sm" onClick={onRetry}>
        <RefreshCw aria-hidden className={cn("mr-2 h-3 w-3", isRetrying && "animate-spin")} />
        {t("slideSense.usage.retry")}
      </Button>
    </div>
  )
}
